import { useState, useEffect } from 'react';
import { thingsboardService } from '../services/thingsboard';

export interface ConnectedDevice {
  id: string;
  label: string;
  toastName?: string;
  top: string;
  left: string;
  width: string;
  height: string;
  colorScheme: 'cyan' | 'amber' | 'emerald';
}

export function useConnectedDevices(apartmentAssetId: string | null) {
  const [devices, setDevices] = useState<ConnectedDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchConnectedDevices() {
      if (!apartmentAssetId || apartmentAssetId === '00000000-0000-0000-0000-000000000000') {
        setDevices([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // 1. Fetch Apartment Relations (Level 1)
        const lvl1Relations = await thingsboardService.getRelations('ASSET', apartmentAssetId);
        
        // 1.1 Collect direct devices (Level 1)
        const lvl1Devices = lvl1Relations
          .filter((r: any) => r.to.entityType === 'DEVICE')
          .map((r: any) => ({
            id: r.to.id,
            name: r.toName || 'Unnamed Device',
            type: '' // Will fetch type in parallel later
          }));

        // 1.2 Identify sub-assets/Zones (Level 1)
        const subAssets = lvl1Relations.filter((r: any) => r.to.entityType === 'ASSET');

        // 2. Fetch Level 2 relations for each sub-asset in parallel
        const lvl2DevicesNested = await Promise.all(subAssets.map(async (aRel: any) => {
          try {
            const rels = await thingsboardService.getRelations('ASSET', aRel.to.id);
            return rels
              .filter((r: any) => r.to.entityType === 'DEVICE')
              .map((r: any) => ({
                id: r.to.id,
                name: r.toName || 'Unnamed Device',
                type: ''
              }));
          } catch (e) {
            console.warn(`Could not fetch relations for child asset ${aRel.to.id}`, e);
            return [];
          }
        }));

        const allCandidateDevices = [...lvl1Devices, ...lvl2DevicesNested.flat()];

        // 3. De-duplicate by ID and fetch type/attributes
        const uniqueDeviceIds = Array.from(new Set(allCandidateDevices.map(d => d.id)));
        
        const enrichedDevices = await Promise.all(uniqueDeviceIds.map(async (deviceId) => {
          try {
            // Fetch Device info (for type) and Attributes (for UI) in parallel
            const [deviceInfo, attrs] = await Promise.all([
              thingsboardService.getDevice(deviceId).catch(() => ({})),
              thingsboardService.getEntityAttributes(
                'DEVICE', 
                deviceId, 
                'SERVER_SCOPE', 
                ['top', 'left', 'width', 'height', 'colorScheme', 'toastName', 'nickName', 'nickname']
              ).catch(() => [])
            ]);

            const type = (deviceInfo.type || '').toUpperCase();
            
            // Filter by requested types: SEC, HERV, smartGlass
            const allowedTypes = ['SEC', 'HERV', 'SMARTGLASS', 'SMARGGLASS'];
            if (!allowedTypes.includes(type)) return null;

            const getAttr = (key: string) => {
              const attr = attrs.find((a: any) => a.key === key);
              return attr ? attr.value : undefined;
            };

            const top = getAttr('top');
            const left = getAttr('left');
            const width = getAttr('width');
            const height = getAttr('height');
            const colorScheme = getAttr('colorScheme') || 'cyan';
            const toastName = getAttr('toastName') || deviceInfo.name;

            return {
              id: deviceId,
              label: deviceInfo.label || deviceInfo.name || 'Unnamed Device',
              toastName: toastName,
              top: typeof top === 'number' ? `${top}%` : (top || '0%'),
              left: typeof left === 'number' ? `${left}%` : (left || '0%'),
              width: typeof width === 'number' ? `${width}%` : (width || '10%'),
              height: typeof height === 'number' ? `${height}%` : (height || '10%'),
              colorScheme: colorScheme as any
            };
          } catch (e) {
            console.error(`Error enriching device ${deviceId}`, e);
            return null;
          }
        }));

        // Filter out nulls (non-matching types or errors)
        setDevices(enrichedDevices.filter(d => d !== null) as ConnectedDevice[]);
      } catch (err: any) {
        console.error('Failed to fetch connected devices:', err);
        setError(err.message || 'Failed to load connected devices');
      } finally {
        setLoading(false);
      }
    }

    fetchConnectedDevices();
  }, [apartmentAssetId]);

  return { devices, loading, error };
}
